import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { WS_URL, TOKEN_KEY, getSocketToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RealtimeCtx = createContext(null);
export const useRealtime = () => useContext(RealtimeCtx);

export function RealtimeProvider({ children }) {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [online, setOnline] = useState({});
  const [connected, setConnected] = useState(false);

  const subscribe = useCallback((cb) => {
    listenersRef.current.add(cb);
    return () => listenersRef.current.delete(cb);
  }, []);

  const handleEvent = useCallback((event, data) => {
    listenersRef.current.forEach((cb) => cb(event, data));
    switch (event) {
      case "notification":
        qc.invalidateQueries({ queryKey: ["notifications"] });
        qc.invalidateQueries({ queryKey: ["unreadCount"] });
        if (data?.title) toast(data.title, { description: data.body });
        break;
      case "new_match":
        qc.invalidateQueries({ queryKey: ["matches"] });
        qc.invalidateQueries({ queryKey: ["chats"] });
        toast.success("Neues Match! 🎉", { description: "Der Chat ist jetzt verfügbar." });
        break;
      case "match_accepted":
        qc.invalidateQueries({ queryKey: ["matches"] });
        qc.invalidateQueries({ queryKey: ["chats"] });
        break;
      case "new_message":
        qc.invalidateQueries({ queryKey: ["chats"] });
        qc.invalidateQueries({ queryKey: ["messages", data?.chatId] });
        break;
      case "trip_created":
        qc.invalidateQueries({ queryKey: ["feed"] });
        qc.invalidateQueries({ queryKey: ["myTrips"] });
        break;
      case "profile_updated":
        qc.invalidateQueries({ queryKey: ["me"] });
        break;
      case "premium_updated":
        refresh();
        qc.invalidateQueries({ queryKey: ["premium"] });
        toast.success(data?.premium ? "Voyalyo Pro aktiviert" : "Voyalyo Pro beendet");
        break;
      case "boost_updated":
        qc.invalidateQueries({ queryKey: ["premium"] });
        break;
      case "presence":
        setOnline((prev) => ({ ...prev, [data.userId]: data.online }));
        break;
      case "account_banned":
        toast.error("Dein Konto wurde gesperrt.");
        break;
      default:
        break;
    }
  }, [qc, refresh]);

  useEffect(() => {
    // Travel-Matching backend has no WebSocket endpoint. Real-time updates are handled
    // via HTTP polling (4s) in ChatPage/NotificationsPage and react-query refetch.
    // WS intentionally disabled to avoid failing handshakes.
    return;
  }, [user, handleEvent]);

  return (
    <RealtimeCtx.Provider value={{ subscribe, online, connected }}>
      {children}
    </RealtimeCtx.Provider>
  );
}
